import api, { authApi } from './api';
import type { User, LoginFormData, RegisterFormData, ApiResponse, UserRole } from '@/types';

interface AuthResponse {
  user: User;
  token: string;
}

interface OAuthUrlResponse {
  oauth_url: string;
}

interface OAuthCallbackData {
  access_token?: string;
  id_token?: string;
  code?: string;
  email?: string;
  name?: string;
  avatar_url?: string;
  provider_user_id?: string;
}

// Backend user attributes format (from login response)
interface BackendUserAttributes {
  id: string;
  email: string;
  name: string;
  status: string;
  role: UserRole;
  region?: string;
  language?: string;
  preferred_llm_preset?: string;
  effective_llm_preset?: string;
  created_at: string;
  updated_at: string;
  sign_in_count?: number;
  last_sign_in_at?: string;
  confirmed_at?: string;
  has_oauth?: boolean;
  oauth_provider?: string;
  is_admin?: boolean;
}

// Backend JSONAPI response format (from /me endpoint)
interface BackendUserResponse {
  id: string;
  type: string;
  attributes: BackendUserAttributes;
}

// Backend login response format
interface BackendLoginResponse {
  user: BackendUserAttributes;
  token: string;
}

// Transform backend user attributes to frontend User type
function transformUserAttributes(attrs: BackendUserAttributes): User {
  return {
    id: attrs.id,
    email: attrs.email,
    name: attrs.name,
    role: attrs.role,
    status: attrs.status as 'active' | 'inactive' | 'suspended',
    region: attrs.region,
    language: attrs.language,
    preferredLlmPreset: attrs.preferred_llm_preset as User['preferredLlmPreset'],
    effectiveLlmPreset: attrs.effective_llm_preset as User['effectiveLlmPreset'],
    createdAt: attrs.created_at,
    updatedAt: attrs.updated_at,
  };
}

// Transform JSONAPI user response to frontend User type
function transformUser(backendUser: BackendUserResponse): User {
  return transformUserAttributes(backendUser.attributes);
}

export type OAuthProvider = 'google' | 'github';

export const authService = {
  // Devise auth endpoints (at root level, not under /api/v1)
  // authApi already has baseURL '/auth', so we use relative paths
  async login(data: LoginFormData): Promise<AuthResponse> {
    console.log('[AUTH] Login request starting...');
    const response = await authApi.post<ApiResponse<BackendLoginResponse>>('/login', { user: data });
    console.log('[AUTH] Login response received:', response);
    console.log('[AUTH] Response data:', response.data);
    console.log('[AUTH] Response data.data:', response.data?.data);
    const result = {
      user: transformUserAttributes(response.data.data.user),
      token: response.data.data.token,
    };
    console.log('[AUTH] Transformed result:', result);
    return result;
  },

  async register(data: RegisterFormData): Promise<AuthResponse> {
    const response = await authApi.post<ApiResponse<BackendLoginResponse>>('/register', {
      name: data.name,
      email: data.email,
      password: data.password,
    });
    return {
      user: transformUserAttributes(response.data.data.user),
      token: response.data.data.token,
    };
  },

  async logout(): Promise<void> {
    await authApi.delete('/logout');
  },

  // API endpoints (under /api/v1)
  async getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<BackendUserResponse>>('/me');
    return transformUser(response.data.data);
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.patch<ApiResponse<BackendUserResponse>>('/me', { user: data });
    return transformUser(response.data.data);
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await authApi.post('/password', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: newPassword
    });
  },

  async requestPasswordReset(email: string): Promise<void> {
    await authApi.post('/password', { email });
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await authApi.put('/password', {
      reset_password_token: token,
      password,
      password_confirmation: password
    });
  },

  // OAuth methods (under /api/v1)
  async getOAuthUrl(provider: OAuthProvider): Promise<string> {
    const redirectUri = `${window.location.origin}/auth/${provider}/callback`;
    const response = await api.get<ApiResponse<OAuthUrlResponse>>(
      `/auth/${provider}`,
      { params: { redirect_uri: redirectUri } }
    );
    return response.data.data.oauth_url;
  },

  async handleOAuthCallback(provider: OAuthProvider, data: OAuthCallbackData): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>(
      `/auth/${provider}/callback`,
      data
    );
    return response.data.data;
  },

  // Utility to initiate OAuth flow
  async initiateOAuth(provider: OAuthProvider): Promise<void> {
    const oauthUrl = await this.getOAuthUrl(provider);
    window.location.href = oauthUrl;
  },
};
